import * as React from 'react';
import { FileText, Plus, Search, XCircle } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  OFFER_STATUSES,
  type Client,
  type ListOffersQuery,
  type Offer,
  type OfferAssignee,
  type OfferListItem,
} from '@ooh/contracts';

import { Button } from '@ooh/ui/components/button';
import { DataTable, type ColumnDef } from '@ooh/ui/components/data-table';
import { usePersistedDataTableColumns } from '../../../hooks/use-persisted-data-table-columns';
import { Input } from '@ooh/ui/components/input';
import { MultiSelectCombobox } from '@ooh/ui/components/multi-select-combobox';
import type { BulkAction } from '@ooh/ui/components/bulk-action-toolbar';
import {
  SearchableSelectField,
  type SearchableSelectFieldOption as SelectOption,
} from '@ooh/ui/components/searchable-select-field';
import { OfferListPagination } from './offer-list-pagination';
import {
  type OfferListRow,
  buildOfferListColumns,
  OfferListBulkSurface,
} from './offer-list-helpers';
import { RowDetailLink } from '../../../components/row-detail-link';

type OfferView = ListOffersQuery['view'];

export const OFFER_LIST_PAGE_SIZE = 25;

interface OfferListPageProps {
  offers: OfferListItem[];
  clients: Client[];
  view?: OfferView;
  canManageAny: boolean;
  canCreate?: boolean;
  assignableUsers?: OfferAssignee[];
  assignedToUserIds?: number[];
  onViewChange: (view: OfferView) => void;
  onAssignedToChange?: (userIds: number[]) => void;
}

const OFFER_LIST_SORT_KEYS = [
  'title',
  'status',
  'campaignNumber',
  'validUntil',
  'brand',
  'updatedAt',
] as const;
type OfferListSortKey = (typeof OFFER_LIST_SORT_KEYS)[number];

// Fixed lifecycle order (DRAFT -> SENT -> ACCEPTED -> CONFIRMED -> EXPIRED), taken
// from OFFER_STATUSES' declaration order in @ooh/contracts. Sorting by the translated
// label instead would flip ordering between languages; this stays stable regardless
// of locale.
const OFFER_STATUS_SORT_ORDER = new Map<Offer['status'], number>(
  OFFER_STATUSES.map((status, index) => [status, index]),
);

/**
 * Compares two nullable values, always placing null/undefined last regardless of the
 * `asc`/`desc` direction — the direction multiplier only applies to genuine value
 * comparisons, not to null placement.
 */
function compareNullableLast<T>(
  left: T | null | undefined,
  right: T | null | undefined,
  compare: (a: T, b: T) => number,
  multiplier: number,
): number {
  if (left == null && right == null) return 0;
  if (left == null) return 1;
  if (right == null) return -1;
  return compare(left, right) * multiplier;
}

/**
 * Parses a `01/09/2026/VM/Żabka/JS`-shaped campaign number's first three
 * segments into a sortable `{ year, month, ordinal }` key. Returns null for
 * a value that doesn't conform to the new shape — the retired
 * `KAM-yyyy-NNN` numbers, or any hand-edited arbitrary string (the
 * campaign-number edit path accepts free-form text up to 64 chars).
 */
function parseCampaignNumberSortKey(
  value: string,
): { year: number; month: number; ordinal: number } | null {
  const [ordinalPart, monthPart, yearPart] = value.split('/');
  if (ordinalPart == null || monthPart == null || yearPart == null) return null;
  if (!/^\d+$/.test(ordinalPart) || !/^\d{2}$/.test(monthPart) || !/^\d{4}$/.test(yearPart)) {
    return null;
  }

  const ordinal = Number(ordinalPart);
  const month = Number(monthPart);
  const year = Number(yearPart);
  if (ordinal <= 0 || month < 1 || month > 12) return null;

  return { year, month, ordinal };
}

/**
 * Compares two non-null campaign numbers. Parsed values (the new
 * ordinal/month/year shape) sort by `(year, month, ordinal)` so a month's
 * offers stay together (offer-approval / "Sorting keeps a month's offers
 * together"). A value that doesn't parse — a legacy `KAM-yyyy-NNN` number or
 * a hand-edited arbitrary string — has no year/month to group by; no spec
 * scenario pins its exact position, so this is an inference, not a
 * spec-stated rule: it sorts after every parseable value, direction-
 * independent (mirroring how a missing number always sorts last regardless
 * of asc/desc), but two unparseable values compared against each other
 * still fall back to a plain locale compare that does flip with direction.
 */
function compareCampaignNumberValues(left: string, right: string, multiplier: number): number {
  const leftKey = parseCampaignNumberSortKey(left);
  const rightKey = parseCampaignNumberSortKey(right);

  if (leftKey && rightKey) {
    if (leftKey.year !== rightKey.year) return (leftKey.year - rightKey.year) * multiplier;
    if (leftKey.month !== rightKey.month) return (leftKey.month - rightKey.month) * multiplier;
    return (leftKey.ordinal - rightKey.ordinal) * multiplier;
  }
  if (leftKey && !rightKey) return -1;
  if (!leftKey && rightKey) return 1;

  return left.localeCompare(right, 'pl') * multiplier;
}

function buildOfferRows(offers: OfferListItem[], clients: Client[]): OfferListRow[] {
  const clientNames = new Map(clients.map((client) => [client.id, client.name]));
  return offers
    .filter((offer) => offer.trashedAt == null)
    .map((offer) => ({
      brand: offer.brand ?? '',
      offer,
      clientName: clientNames.get(offer.clientId) ?? '',
    }));
}

function filterOfferRows(rows: OfferListRow[], searchQuery: string): OfferListRow[] {
  const normalizedQuery = searchQuery.trim().toLowerCase();
  if (normalizedQuery.length === 0) return rows;

  return rows.filter(({ brand, offer, clientName }) => {
    return (
      brand.toLowerCase().includes(normalizedQuery) ||
      offer.title.toLowerCase().includes(normalizedQuery) ||
      clientName.toLowerCase().includes(normalizedQuery)
    );
  });
}

function sortOfferRows(
  rows: OfferListRow[],
  sortBy: OfferListSortKey,
  sortDir: 'asc' | 'desc',
): OfferListRow[] {
  const multiplier = sortDir === 'asc' ? 1 : -1;
  return [...rows].sort((left, right) => {
    if (sortBy === 'brand') {
      return left.brand.localeCompare(right.brand, 'pl') * multiplier;
    }
    if (sortBy === 'title') {
      return left.offer.title.localeCompare(right.offer.title, 'pl') * multiplier;
    }
    if (sortBy === 'status') {
      return (
        ((OFFER_STATUS_SORT_ORDER.get(left.offer.status) ?? 0) -
          (OFFER_STATUS_SORT_ORDER.get(right.offer.status) ?? 0)) *
        multiplier
      );
    }
    if (sortBy === 'campaignNumber') {
      // Groups numbered offers by year then month before ordinal within the
      // month (offer-approval / "Sorting keeps a month's offers together").
      // The direction multiplier is applied inside compareCampaignNumberValues
      // itself (not here) so a parsed-vs-unparsed decision stays
      // direction-independent while year/month/ordinal comparisons still flip.
      return compareNullableLast(
        left.offer.campaignNumber,
        right.offer.campaignNumber,
        (a, b) => compareCampaignNumberValues(a, b, multiplier),
        1,
      );
    }
    if (sortBy === 'validUntil') {
      // validUntil is a required field (z.date(), non-nullable) per @ooh/contracts,
      // so a plain date compare is sufficient — no null-handling needed here.
      return (left.offer.validUntil.getTime() - right.offer.validUntil.getTime()) * multiplier;
    }

    return (left.offer.updatedAt.getTime() - right.offer.updatedAt.getTime()) * multiplier;
  });
}

export function OfferListPendingState() {
  const { t } = useTranslation('offers');
  return <div className="p-6 text-sm text-muted-foreground">{t('list.loading')}</div>;
}

export function OfferListErrorState({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('offers');

  return (
    <div className="flex flex-col items-center justify-center gap-4 p-6 text-center">
      <p className="text-sm text-destructive">{t('list.error')}</p>
      <Button size="sm" type="button" onClick={onRetry}>
        {t('list.retry')}
      </Button>
    </div>
  );
}

function OfferListEmptyState({ canCreate }: { canCreate: boolean }) {
  const { t } = useTranslation('offers');

  return (
    <div
      className="flex flex-col items-center justify-center px-8 py-24 text-center"
      data-testid="offer-list-empty-state"
    >
      <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-muted/60">
        <FileText className="h-8 w-8 text-muted-foreground/60" strokeWidth={1.5} />
      </div>
      <h2 className="mb-2 text-[15px] font-semibold">{t('list.emptyState.title')}</h2>
      <p className="mb-8 max-w-sm text-[13px] text-muted-foreground">
        {t('list.emptyState.description')}
      </p>
      {canCreate ? (
        <Button asChild>
          <Link to="/offers/new" data-testid="offer-list-empty-cta">
            <Plus className="h-4 w-4" />
            {t('list.actions.createFirstDraft')}
          </Link>
        </Button>
      ) : null}
    </div>
  );
}

export function OfferListPage({
  offers,
  clients,
  view = 'mine',
  canManageAny,
  canCreate = true,
  assignableUsers = [],
  assignedToUserIds = [],
  onViewChange,
  onAssignedToChange,
}: OfferListPageProps) {
  const { t } = useTranslation('offers');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [sortBy, setSortBy] = React.useState<OfferListSortKey>('updatedAt');
  const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('desc');
  const [page, setPage] = React.useState(1);
  const [selectedIds, setSelectedIds] = React.useState<Set<string | number>>(new Set());
  const [withdrawBulkOpen, setWithdrawBulkOpen] = React.useState(false);

  const allRows = React.useMemo(() => buildOfferRows(offers, clients), [offers, clients]);

  const filteredRows = React.useMemo(() => {
    return sortOfferRows(filterOfferRows(allRows, searchQuery), sortBy, sortDir);
  }, [allRows, searchQuery, sortBy, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / OFFER_LIST_PAGE_SIZE));

  // Reset to the first page whenever the filtered result set changes.
  // Depend on the joined id string, not the array reference: the
  // assignedToUserIds prop defaults to a fresh [] on every render when the
  // caller omits it, which would otherwise reset the page on every render.
  const assignedToUserIdsKey = assignedToUserIds.join(',');
  React.useEffect(() => {
    setPage(1);
  }, [searchQuery, sortBy, sortDir, view, assignedToUserIdsKey]);

  const safePage = Math.min(page, totalPages);
  const pageRows = React.useMemo(
    () =>
      filteredRows.slice((safePage - 1) * OFFER_LIST_PAGE_SIZE, safePage * OFFER_LIST_PAGE_SIZE),
    [filteredRows, safePage],
  );

  const scopeOptions = React.useMemo<SelectOption[]>(() => {
    const options: SelectOption[] = [
      { value: 'mine', label: t('list.views.mine') },
      { value: 'shared-with-me', label: t('list.views.sharedWithMe') },
    ];
    if (canManageAny) {
      options.push({ value: 'all', label: t('list.views.all') });
    }
    return options;
  }, [canManageAny, t]);

  const assigneeOptions = React.useMemo(
    () => assignableUsers.map((user) => ({ value: String(user.id), label: user.name })),
    [assignableUsers],
  );
  const selectedAssigneeValues = React.useMemo(
    () => assignedToUserIds.map(String),
    [assignedToUserIds],
  );
  const showAssignedToFilter = canManageAny && view === 'all';

  const columns = React.useMemo<ColumnDef<OfferListRow>[]>(() => buildOfferListColumns(t), [t]);
  const persistedTable = usePersistedDataTableColumns(columns, 'ooh:offers-list');

  // Bulk withdraw-acceptance (client-offer-decision-persistence / Bulk
  // withdrawal surface in the internal offer list). Non-ACCEPTED rows are
  // excluded from the payload rather than silently attempted — per-entry
  // authorization is enforced server-side, reported per offer in the result.
  // Derived from the unfiltered `allRows`, not `filteredRows`, so a later
  // search doesn't silently drop an already-selected offer from the payload
  // (row-selection spec: bulk actions act on the full selection).
  const selectedAcceptedRows = React.useMemo(
    () =>
      allRows.filter(
        (row) => selectedIds.has(String(row.offer.id)) && row.offer.status === 'ACCEPTED',
      ),
    [allRows, selectedIds],
  );
  // Rows the current search shows, shared by hiddenAcceptedCount (the withdraw
  // dialog's ACCEPTED-only payload) and selectedVisibleCount (the toolbar's
  // whole-selection "N selected, M visible" line) below, so both answer "is
  // this offer currently shown" the same way.
  const shownIds = React.useMemo(
    () => new Set(filteredRows.map((row) => String(row.offer.id))),
    [filteredRows],
  );
  const hiddenAcceptedCount = React.useMemo(
    () => selectedAcceptedRows.filter((row) => !shownIds.has(String(row.offer.id))).length,
    [shownIds, selectedAcceptedRows],
  );
  const selectedVisibleCount = React.useMemo(() => {
    let visible = 0;
    for (const id of selectedIds) if (shownIds.has(String(id))) visible++;
    return visible;
  }, [shownIds, selectedIds]);
  // A selected ACCEPTED row the caller can only view (a share grant without
  // edit permission) is excluded from the payload the same way a
  // non-ACCEPTED row is: the server enforces this same rule per entry
  // (assertPublishPermission), but surfacing it here means the caller
  // discovers it before submitting rather than mixed into the result dialog.
  const selectedAcceptedOffers = React.useMemo(
    () =>
      selectedAcceptedRows
        .filter((row) => row.offer.effectiveAccess === 'view+edit')
        .map((row) => row.offer),
    [selectedAcceptedRows],
  );
  const bulkActions = React.useMemo<BulkAction[]>(() => {
    const disabled = selectedAcceptedOffers.length === 0;
    // Distinguishes "nothing ACCEPTED selected" from "ACCEPTED rows selected
    // but every one is view-only" — the latter needs a different explanation
    // since the toolbar otherwise looks like it should be enabled.
    const disabledTooltip = !disabled
      ? undefined
      : selectedAcceptedRows.length > 0
        ? t('list.bulkWithdrawAcceptance.toolbarActionAccessRequiredHint')
        : t('list.bulkWithdrawAcceptance.toolbarActionDisabledHint');

    return [
      {
        id: 'withdraw-acceptance',
        label: t('list.bulkWithdrawAcceptance.toolbarAction'),
        icon: <XCircle />,
        disabled,
        disabledTooltip,
        onClick: () => setWithdrawBulkOpen(true),
      },
    ];
  }, [selectedAcceptedOffers.length, selectedAcceptedRows.length, t]);

  function handleSort(nextSortBy: string) {
    if (!(OFFER_LIST_SORT_KEYS as readonly string[]).includes(nextSortBy)) return;
    const key = nextSortBy as OfferListSortKey;

    if (sortBy === key) {
      setSortDir((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortBy(key);
    setSortDir(key === 'updatedAt' ? 'desc' : 'asc');
  }

  return (
    <div className="flex h-full flex-col" data-testid="offer-list-page">
      <div className="flex items-center gap-3 border-b border-border bg-background px-6 py-3">
        <div className="relative max-w-sm flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            aria-label={t('list.searchAriaLabel')}
            className="h-9 pl-9 text-[13px]"
            data-testid="offers-search-input"
            placeholder={t('list.searchPlaceholder')}
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
          />
        </div>

        <div className="ml-auto flex items-center gap-2">
          <div className="w-44">
            <SearchableSelectField
              aria-label={t('list.views.scopeAriaLabel')}
              data-testid="offer-view-scope"
              options={scopeOptions}
              value={view}
              onChange={(next) => onViewChange(next as OfferView)}
            />
          </div>
          {showAssignedToFilter ? (
            <div className="w-56">
              <MultiSelectCombobox
                aria-label={t('list.assignedTo.ariaLabel')}
                data-testid="offer-assigned-to-filter"
                emptyText={t('list.assignedTo.empty')}
                options={assigneeOptions}
                placeholder={t('list.assignedTo.placeholder')}
                searchPlaceholder={t('list.assignedTo.searchPlaceholder')}
                selectedValues={selectedAssigneeValues}
                onChange={(values) => onAssignedToChange?.(values.map(Number))}
              />
            </div>
          ) : null}
          {canCreate ? (
            <Button asChild type="button">
              <Link to="/offers/new" data-testid="offer-new-draft-link">
                <Plus className="h-4 w-4" />
                {t('list.actions.newDraft')}
              </Link>
            </Button>
          ) : null}
        </div>
      </div>

      <div className="flex flex-1 flex-col overflow-hidden">
        {filteredRows.length === 0 ? (
          <OfferListEmptyState canCreate={canCreate} />
        ) : (
          <>
            <div className="flex-1 overflow-y-auto">
              <DataTable
                className="rounded-none border-0"
                columns={persistedTable.columns}
                data={pageRows}
                getRowKey={(row: OfferListRow) => String(row.offer.id)}
                getRowTestId={(row: OfferListRow) => `offer-row-${row.offer.id}`}
                onSort={handleSort}
                rowSelection={{ selectedIds, onChange: setSelectedIds }}
                renderRowLink={(row: OfferListRow) => (
                  <RowDetailLink
                    to="/offers/$offerId"
                    params={{ offerId: String(row.offer.id) }}
                    // eslint-disable-next-line i18next/no-literal-string -- route search param value, not user-facing text
                    search={{ tab: 'units' }}
                    ariaLabel={t('list.rowLinkAriaLabel', { title: row.offer.title })}
                    testId={`offer-detail-link-${row.offer.id}`}
                  />
                )}
                sortBy={sortBy}
                sortDir={sortDir}
                onColumnResizeEnd={persistedTable.onColumnResizeEnd}
                // The toolbar above sits OUTSIDE this `overflow-y-auto` div (a sibling
                // before it, fixed in place by flex layout already) — it is not inside
                // the scroll boundary DataTable's sticky header clamps against, so there
                // is no toolbar height to clear here. Offset 0 sticks the header flush
                // to this container's own top (contrast units-tab, where the toolbar
                // genuinely shares the table's scroll container).
                stickyHeaderOffsetPx={0}
              />
            </div>
            <OfferListPagination page={safePage} totalPages={totalPages} onPageChange={setPage} />
          </>
        )}
      </div>

      <OfferListBulkSurface
        selectedCount={selectedIds.size}
        visibleCount={selectedVisibleCount}
        bulkActions={bulkActions}
        onClearSelection={() => setSelectedIds(new Set())}
        withdrawBulkOpen={withdrawBulkOpen}
        onCloseWithdrawBulk={() => {
          setWithdrawBulkOpen(false);
          setSelectedIds(new Set());
        }}
        selectedAcceptedOffers={selectedAcceptedOffers}
        hiddenCount={hiddenAcceptedCount}
        t={t}
      />
    </div>
  );
}
